import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, RefreshCw } from 'lucide-react';
import api from '../../services/api';
import { useTheme } from '../../context/ThemeContext';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8000';

export default function DashboardPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { isDark } = useTheme();

    const [meta, setMeta] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [ticket, setTicket] = useState(null);
    const [iframeLoaded, setIframeLoaded] = useState(false);
    const [timeLeft, setTimeLeft] = useState(null);
    const [refreshKey, setRefreshKey] = useState(0);

    const token = localStorage.getItem('access_token');
    const startRef = useRef(Date.now()); // vaqt hisoblash uchun

    // Load meta from /me/dashboards — uses Bearer token via axios
    useEffect(() => {
        api.get('/me/dashboards')
            .then(res => {
                const found = res.data.find(d => d.id === id);
                if (!found) throw new Error('Bu panelga kirish ruxsati yo\'q');
                setMeta(found);
            })
            .catch(e => setError(e.message || 'Xatolik'))
            .finally(() => setLoading(false));

        // Fetch Zero-Trust Ticket for the iframe
        let mounted = true;
        api.post(`/panel/ticket/${id}`)
            .then(res => {
                if (mounted) setTicket(res.data.ticket);
            })
            .catch(e => {
                if (mounted) setError(e.response?.data?.detail || "Could not fetch ticket");
            });

        return () => { mounted = false; };
    }, [id]);

    // On unmount or visibility change: log dashboard view activity (fire-and-forget with beacon)
    useEffect(() => {
        let hasSent = false;
        startRef.current = Date.now();

        const sendActivity = (isUnload = true) => {
            if (hasSent || !startRef.current) return;
            hasSent = true;

            const duration = Math.round((Date.now() - startRef.current) / 1000);
            if (duration >= 3 && token) {
                if (isUnload) {
                    const url = new URL(`${API_BASE}/panel/activity`, window.location.origin);
                    url.searchParams.append('token', token);
                    url.searchParams.append('subcategory_id', id);
                    url.searchParams.append('duration_seconds', duration);

                    if (navigator.sendBeacon) {
                        navigator.sendBeacon(url.toString());
                    } else {
                        fetch(url.toString(), { method: 'POST', keepalive: true }).catch(() => { });
                    }
                } else {
                    api.post('/panel/activity', {
                        subcategory_id: id,
                        duration_seconds: duration
                    }).catch(() => { });
                }
            }
            startRef.current = null; // stop timer once sent
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'hidden') {
                sendActivity(true);
            } else if (document.visibilityState === 'visible') {
                // user came back, restart timer and allow sending again
                hasSent = false;
                startRef.current = Date.now();
            }
        };

        const handleBeforeUnload = () => {
            sendActivity(true);
        };

        window.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('beforeunload', handleBeforeUnload);
            // Boshqa React sahifaga o'tganda jo'natish
            sendActivity(false);
        };
    }, [id, token]);

    // Timer effect
    useEffect(() => {
        if (!meta || !meta.update_interval) {
            setTimeLeft(null);
            return;
        }
        setTimeLeft(meta.update_interval);
        const timer = setInterval(() => {
            setTimeLeft(prev => {
                if (prev === null) return null;
                if (prev <= 1) {
                    setRefreshKey(k => k + 1);
                    setIframeLoaded(false);
                    return meta.update_interval;
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(timer);
    }, [meta]);

    const themeParam = isDark ? 'dark' : 'light';
    const iframeSrc = ticket
        ? `${API_BASE}/panel/iframe/${id}?ticket=${encodeURIComponent(ticket)}&theme=${themeParam}`
        : null;

    // Show loading spinner when theme changes
    useEffect(() => {
        if (ticket) setIframeLoaded(false);
    }, [themeParam, ticket]);

    if (loading) return (
        <div className="w-full min-h-[500px] flex items-center justify-center bg-surface rounded-2xl border border-border">
            <Loader2 className="w-8 h-8 animate-spin text-brand" />
        </div>
    );

    if (error) return (
        <div className="w-full min-h-[500px] flex flex-col items-center justify-center bg-surface rounded-2xl border border-border gap-4">
            <p className="text-error font-medium">{error}</p>
            <button
                onClick={() => navigate(-1)}
                className="text-brand hover:underline font-semibold flex items-center gap-2"
            >
                <ArrowLeft className="w-4 h-4" /> Orqaga qaytish
            </button>
        </div>
    );

    return (
        <div className="w-full flex flex-col gap-4">
            {/* Top bar */}
            <div className="flex items-center gap-4">
                <button
                    onClick={() => navigate('/panel')}
                    className="p-2 rounded-lg bg-surface hover:bg-surface-2 border border-border text-muted hover:text-primary transition-colors"
                >
                    <ArrowLeft className="w-5 h-5" />
                </button>
                <div className="flex-1">
                    <div className="flex items-center gap-3">
                        <h2 className="text-xl font-bold text-primary leading-tight">{meta?.name_ru}</h2>
                        {timeLeft !== null && (
                            <span
                                className="flex items-center gap-1.5 px-2.5 py-1 bg-surface-2 border border-border rounded-lg text-xs font-semibold text-brand"
                                title="Auto refresh timer"
                            >
                                <RefreshCw className={`w-3.5 h-3.5 text-brand ${timeLeft <= 3 ? 'animate-spin' : ''}`} />
                                {timeLeft}s
                            </span>
                        )}
                    </div>
                    <span className="text-xs text-muted uppercase tracking-wider">{meta?.category_name_ru}</span>
                </div>
            </div>

            {/* Secure iframe */}
            <div
                className="relative w-full rounded-2xl border border-border overflow-hidden shadow-sm bg-surface-2"
                style={{ height: 'calc(100vh - 180px)', minHeight: 500 }}
            >
                {!iframeLoaded && (
                    <div className="absolute inset-0 flex items-center justify-center bg-surface z-10">
                        <Loader2 className="w-8 h-8 animate-spin text-brand" />
                    </div>
                )}
                {iframeSrc && (
                    <iframe
                        key={`${refreshKey}-${themeParam}`}
                        src={iframeSrc}
                        className="w-full h-full border-0"
                        onLoad={() => setIframeLoaded(true)}
                        title={meta?.name_ru || 'Dashboard'}
                        allowFullScreen
                        referrerPolicy="no-referrer"
                    />
                )}
            </div>
        </div>
    );
}
